/* The booklet, drawn and bound.
 *
 * THE ILLUSTRATIONS ARE THE GAME'S. Every sprite, tile and icon on these
 * pages is produced by calling `src/art.ts` — the same functions the game
 * draws with — into a small canvas, scaled up with nearest-neighbour. A manual
 * with its own pictures goes out of date the first time the art changes and
 * nobody notices for a year; this one cannot, because there is only one set of
 * pictures.
 *
 * THE WORDS are in `content.js`, apart from the page, so `test/soak.ts` can
 * read them without a DOM and check them against the game's own tables: add an
 * enemy, a charm, a tile or a shop item and forget the manual, and the suite
 * says so.
 *
 * THE BINDING is a stack of leaves on a hinge. Each leaf carries two pages —
 * a front and a back, the back pre-rotated 180 degrees with its face hidden —
 * so turning one is a single rotateY and the page behind is simply revealed.
 * The only fiddly part is the stacking order, which has to be recomputed on
 * every turn or the leaf you are turning passes through the ones beside it.
 */
import {
  T, TS, tile, sprite, flameSprite, BIOMES, UI,
  drawGlim, drawSigil, drawPod, drawSeedBig, drawLeaf, drawSickle, drawWeapon,
} from '../src/art.ts';
import { STORY, CONTROLS, CHARMS, BESTIARY, LEGEND, NIGHT, GEAR, MAKING, RECIPE_ROWS } from './content.ts';
import { binding } from './binding.ts';
import { HOTBAR, BAG_SLOTS } from '../src/sim/inventory.ts';   // the bag's own numbers, so the sentence cannot drift (2026-09-18)

// ------------------------------------------------------------------ helpers
/** `cls` and `text` are both optional — most nodes are containers. */
const h = (tag, cls?, text?) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};

/** A canvas of `w`x`h` GAME pixels, scaled up, with `draw` run on it. */
function art(w, h2, scale, draw, bg = null) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h2;
  c.style.width = (w * scale) + 'px';
  c.style.height = (h2 * scale) + 'px';
  const g = c.getContext('2d');
  g.imageSmoothingEnabled = false;
  if (bg) { g.fillStyle = bg; g.fillRect(0, 0, w, h2); }
  draw(g);
  return c;
}
const spriteArt = (name, scale = 4) => art(
  sprite(name).width, sprite(name).height, scale,
  (g) => g.drawImage(sprite(name), 0, 0), UI.ink);
const tileArt = (kind, pal, palKey, scale = 3) => art(
  TS, TS, scale, (g) => g.drawImage(tile(kind, pal, palKey, 0, 0), 0, 0));
const plate = (node, margin = '12px auto') => {
  const p = h('div', 'plate'); p.style.margin = margin; p.appendChild(node); return p;
};

/** One page of the control table. */
/** The controls table: a fresh table with its header on every page it takes. */
const controlsWrap = (): [any, any] => {
  const table = h('table');
  const thead = h('thead'), hr = h('tr');
  ['', 'Keyboard', 'Touch'].forEach((t) => hr.appendChild(h('th', null, t)));
  thead.appendChild(hr); table.appendChild(thead);
  const tb = h('tbody'); table.appendChild(tb);
  return [table, tb];
};
const controlRow = (c: any) => {
  const tr = h('tr');
  tr.appendChild(h('td', null, c.do));
  tr.appendChild(h('td', 'k', c.key));
  tr.appendChild(h('td', null, c.touch));
  return tr;
};

// -------------------------------------------------------------------- pages
/** Each entry builds one page. Order is the reading order. */
/* THE BOOK POURS ITS OWN PAGES (2026-09-18).
 *
 * A `.sheet` is a leaf of a 560px page with `overflow: hidden`, and the page builders poured generated lists into it:
 * measured on the glass, six sheets overflowed and what ran past the fold was simply not drawn. Eight of fourteen
 * creatures, two of the three Great Beasts and forty-one of forty-nine recipes were in the booklet's data and on no
 * page of the booklet. Hand-picked slice indices are what rotted -- `TRASH.slice(3)` was written when there were six
 * creatures -- so the slicing is done by MEASUREMENT now: a flowing page is a title, a lede and a list of thunks,
 * and `expand()` fills a hidden leaf of the real size with them until it overflows, then starts another page. The
 * binding already takes any number of pages, and a list that grows now costs a page instead of losing its tail. */
type Wrap = () => [any, any];   // [what goes on the page, what the items go into]
type Flow = { flow: true; title: string; lede?: string; note?: string; wrap?: Wrap; intro?: Array<() => any>; items: Array<() => any> };
const flow = (title: string, items: Array<() => any>, opts: { lede?: string; note?: string; wrap?: Wrap; intro?: Array<() => any> } = {}): Flow =>
  ({ flow: true, title, items, ...opts });
/** A grid of swatch rows, the booklet's own `.legend`. */
const legendWrap: Wrap = () => { const g = h('div', 'legend'); return [g, g]; };
const NUMERAL = ['', '  ·  ii', '  ·  iii', '  ·  iv', '  ·  v', '  ·  vi', '  ·  vii', '  ·  viii', '  ·  ix', '  ·  x', '  ·  xi', '  ·  xii'];

const PAGES: any[] = [
  // 1 — the cover
  (el) => {
    el.classList.add('cover');
    el.appendChild(h('div', 'sub', 'instruction booklet'));
    el.appendChild(h('h1', null, 'rako'));
    el.appendChild(h('div', 'sub', 'a procedural adventure for one forager'));
    el.appendChild(plate(art(48, 40, 4, (g) => {
      g.fillStyle = UI.ink; g.fillRect(0, 0, 48, 40);
      drawSeedBig(g, 12, 6, true);
    })));
    el.appendChild(h('div', 'badge', 'three great beasts'));
    return { plain: true };
  },
  // 2 — the wilds (as many pages as the story takes)
  flow('the wilds', [
    ...STORY.map((para, i) => () => h('p', i === 0 ? 'lede' : null, para)),
    () => plate(art(TS * 6, TS, 3, (g) => {
      const pal = BIOMES.heath;
      [T.GROUND, T.MOSS, T.SAND, T.TREE, T.ROCK, T.WATER].forEach((k, i) => {
        g.drawImage(tile(k, pal, 'heath', 0, 0), i * TS, 0);
      });
    }), '10px auto'),
  ], { note: 'Every world is built from a seed word. The same word always builds the same wilds, so a world worth '
    + 'playing is a world worth writing down.' }),
  /* 3, 4, 5 — controls. SPLIT ACROSS THREE PAGES: six rows is what fits on a
   * paperback page, and a table that runs off the bottom of a page in a
   * printed manual is a manual that lies about what the game does.
   *
   * IT WAS TWO PAGES AND EIGHT ROWS SHORT (the lore lens, round 51). The
   * comment above used to say "ten rows does not fit on one page", which was
   * true when CONTROLS held ten. It holds EIGHTEEN, and the second page
   * stopped at row 10 -- so the bag (e), the map (m), the next slot (c or q),
   * start (enter), a different world (r), silence (shift-m), the picture
   * filter (f) and every key you use INSIDE the bag were printed nowhere,
   * while two later pages talk about the bag and the map as though the
   * booklet had taught them. The end index is CONTROLS.length now, so the
   * table cannot fall behind the table it prints again. */
  /* AND THE TABLE POURS ITSELF (2026-09-18): six rows a page was measured when CONTROLS held ten, and the first of
   * the three hand-split pages ran 395 px past the fold. Each page takes the rows that fit, with its own header. */
  flow('controls', CONTROLS.map((c: any) => () => controlRow(c)), { wrap: controlsWrap,
    note: 'Everything you carry is a stack in the bag: ' + HOTBAR + ' slots along the top and '
      + (BAG_SLOTS - HOTBAR) + ' more behind them. What is picked is what your hands do -- a weapon swings, a charm '
      + 'works, sap is eaten -- and the use button always says which.' }),
  // 4 — the sickle and vigor
  flow('the sickle', [
    () => h('p', null, 'It swings in an arc and cuts what is in front of you. Leave it alone for a moment and the '
      + 'gauge beside your vigor fills; then the same button throws it instead, and it comes back.'),
    () => plate(art(40, 40, 3, (g) => {
      g.fillStyle = UI.ink; g.fillRect(0, 0, 40, 40);
      g.drawImage(sprite('forager_side_0'), 12, 12);
      drawSickle(g, 20, 20, -0.5, 14, true, true);
    })),
  ]),
  /* VIGOR HAS ITS OWN PAGE (2026-09-18). It shared the sickle's, and when the pour measured them together the last
   * picture went to a page of its own with nothing else on it. Two topics, two pages, both full. */
  /* FIVE LEAVES, WHATEVER YOUR VIGOR: the row used to add a leaf for every two points, which meant that by the
   * second beast it was a row of rectangles four pixels wide. It is five leaves and each carries a fifth of what you
   * have, so the picture in the booklet is the picture on the glass at any point in a run. */
  flow('vigor', [
    () => h('p', null, 'The five leaves along the top are what you have left, however much vigor you have: each one '
      + 'carries a fifth of it, and empties from the right. Each great beast you fell puts more life behind them, '
      + 'and sap puts back what you lost.'),
    () => plate(art(60, 12, 3, (g) => {
      g.fillStyle = UI.hud; g.fillRect(0, 0, 60, 12);
      for (let i = 0; i < 5; i++) {
        drawLeaf(g, 1 + i * 9, 2, 8, UI.inkSoft, UI.inkSoft);
        if (i < 3) drawLeaf(g, 1 + i * 9, 2, 8, UI.leaf, UI.leafDim);
      }
    }), '10px auto'),
    () => h('p', 'note', 'Ten glim pressed is one more point of vigor, to a ceiling of twenty-six; a leaf is worth '
      + 'more as the ceiling rises, and the row stays five leaves long.'),
  ]),
];

/** The three charms, two to a page. */
// ...AND THESE ARE THE GAME'S TOO. The first draft hand-drew the water and
// the fire as coloured rectangles, which made them the only pictures in the
// book that could drift from what a player actually sees. Each charm is now
// shown as the thing it acts on or produces: the pod it sets down, the deep
// water it takes you under, the flame it leaves behind.
const CHARM_ICON = {
  pods: (g) => drawPod(g, 4, 4),
  tide: (g) => g.drawImage(tile(T.WATER, BIOMES.heath, 'heath', 0, 0), 0, 0),
  ember: (g) => g.drawImage(flameSprite(1), 4, 3),
};
function charmFigure(c, titled = false) {
  const fig = h('div', 'fig');
  const box = h('div', 'art');
  box.appendChild(art(16, 16, 4, CHARM_ICON[c.tool], UI.ink));
  const body = h('div', 'body');
  // when the page is already titled with the charm, do not say its name twice
  body.appendChild(h('h3', null, titled ? 'the button reads ' + c.label
    : c.name + '   —   ' + c.label));
  body.appendChild(h('p', null, c.what));
  body.appendChild(h('p', null, c.more));
  // THE SECOND VERB. Each charm reads the situation, and the second answer is
  // the one that makes it a tool rather than a permission — so it gets its own
  // heading rather than being buried in the paragraph above.
  if (titled && c.also) {
    body.appendChild(h('h3', null, 'and the other way'));
    body.appendChild(h('p', null, c.also));
  }
  fig.appendChild(box); fig.appendChild(body);
  return fig;
}
// ALL THREE FIRST, THEN ONE TO A PAGE.
//
// Two charms to a page overflowed by 48px, so I split them one per page — and
// that was worse in a way the measurement could not see: every page then said
// "The Three Charms" at the top and showed ONE, with nothing to say the other
// two were coming. A reader landing on any of them reasonably concluded the
// game had one tool. A contents spread fixes it: you see that there are three
// and what each opens before you read about any of them, and each detail page
// is then titled with the charm's own name and numbered.
PAGES.push((el) => {
  el.appendChild(h('h2', null, 'the three charms'));
  el.appendChild(h('p', 'lede', 'They lie in the wilds as caches, like the '
    + 'spear, the axe and the torch: the nearer ones a screen or two from where '
    + 'you wake, the farther ones out where the beasts walk. Nothing is gated; '
    + 'you can go anywhere from the first step.'));
  for (const c of CHARMS) {
    const fig = h('div', 'fig');
    const box = h('div', 'art');
    box.appendChild(art(16, 16, 3, CHARM_ICON[c.tool], UI.ink));
    const body = h('div', 'body');
    body.appendChild(h('h3', null, c.name + '   —   ' + c.label));
    body.appendChild(h('p', null, c.what));
    fig.appendChild(box); fig.appendChild(body);
    el.appendChild(fig);
  }
  el.appendChild(h('p', 'note', 'None of them opens a door; there are no doors. Each is a way '
    + 'to move, or to move the world. The next three pages take one each.'));
});
CHARMS.forEach((c, i) => PAGES.push((el) => {
  el.appendChild(h('h2', null, c.name));
  el.appendChild(h('p', 'eyebrow', 'the three charms  ·  ' + (i + 1) + ' of ' + CHARMS.length));
  el.appendChild(charmFigure(c, true));
}));

/** WHAT IS WORN (LS-239), generated from the game's table and poured over as many pages as it takes (2026-09-18).
 *  The lede names the three slots and counts nothing: the count was five when it was written and is thirty-two now. */
PAGES.push(flow('what is worn', GEAR.map((c) => () => {
  const fig = h('div', 'fig');
  const box = h('div', 'art');
  box.appendChild(art(16, 16, 3, (g) => drawWeapon(g, c.kind, 1, 1, true), UI.ink));
  const body = h('div', 'body');
  body.appendChild(h('h3', null, c.name + '   —   ' + c.slot));
  body.appendChild(h('p', null, c.what));
  fig.appendChild(box); fig.appendChild(body);
  return fig;
}), {
  lede: 'Three slots beside the bag: body, feet, charm — one piece in each. A beast leaves what it was made of; the '
    + 'rest lie in caches, or are forged at a fire. Pick a piece and use it to wear it; what was worn there comes '
    + 'back into the picked slot. In the bag, the worn column shows what is on, and a tap there (or x on an empty '
    + 'slot) takes it off.',
}));

/** The tile legend, poured so neither page is a wall of swatches and none of it falls off the bottom. */
const legendRow = (item: any) => {
  const row = h('div', 'item');
  const sw = h('div', 'swatch');
  sw.appendChild(tileArt(T[item.tile], BIOMES.heath, 'heath', 3));
  const txt = h('div');
  txt.appendChild(h('b', null, item.name));
  txt.appendChild(h('span', null, item.note));
  row.appendChild(sw); row.appendChild(txt);
  return row;
};
/** Making things: the forge, and the one table of what it makes (2026-09-12). */
/* THE FORGE'S OWN TABLE, POURED (2026-09-18): forty-nine recipes went into one 560px page and eight of them were
 * drawn. The prose and the rows are one list now, and the list takes the pages it needs. */
PAGES.push(flow('making things', RECIPE_ROWS.map((r) => () => {
  const row = h('div', 'item');
  const txt = h('div');
  txt.appendChild(h('b', null, r.made));
  txt.appendChild(h('span', null, 'from ' + r.from));
  row.appendChild(txt);
  return row;
}), { wrap: legendWrap, intro: [() => h('p', 'eyebrow', 'the forge'), ...MAKING.map((para) => () => h('p', null, para))] }));
PAGES.push(flow('reading the land', LEGEND.map((item) => () => legendRow(item)), { wrap: legendWrap }));

/** The bestiary. */
function beastFigure(b) {
  const fig = h('div', 'fig');
  const box = h('div', 'art');
  box.appendChild(spriteArt(b.art, b.boss ? 2 : 4));
  const body = h('div', 'body');
  body.appendChild(h('h3', null, b.name));
  body.appendChild(h('p', null, b.note));
  fig.appendChild(box); fig.appendChild(body);
  return fig;
}
const TRASH = BESTIARY.filter((b) => !b.boss);
/* `TRASH.slice(3)` was written when there were six creatures; there are fourteen, and eight of them fell off the
 * bottom of the second page (2026-09-18). Both pages are poured now. */
PAGES.push(flow('things that live here', TRASH.map((b) => () => beastFigure(b))));
const GREAT = BESTIARY.filter((b) => b.boss);
PAGES.push(flow('the three great beasts', GREAT.map((b) => () => beastFigure(b)), {
  lede: 'Each roams a territory five screens across around its lair; the map shows a rumour of it until you have '
    + 'seen it, and a skull after. Each has its own answer.',
}));

PAGES.push(flow('the long night', NIGHT.map((para: string, i: number) => () => h('p', i === 0 ? 'lede' : null, para)), {
  note: 'Seeds are shareable. The address bar holds the one you are playing, so a world you liked is a link.' }));
PAGES.push((el) => {
  el.classList.add('cover');
  el.appendChild(h('div', 'sub', 'and when you have read enough'));
  el.appendChild(h('h1', null, 'go and try it'));
  el.appendChild(h('p', null, 'The playground puts every charm, every creature '
    + 'and every kind of ground in one room, with nothing trying to kill you.'));
  const a = h('a', null, 'open the playground');
  a.href = '../playground/';
  a.style.cssText = 'margin-top:14px;padding:8px 16px;border:2px solid #2b2418;'
    + 'border-radius:999px;font-size:10px;letter-spacing:.22em;'
    + 'text-transform:uppercase;color:#2b2418;text-decoration:none';
  el.appendChild(a);
  return { plain: true };
});

// ------------------------------------------------------------------- bind
//
// TWO PAGES TO A LEAF IS ONLY TRUE WHEN YOU CAN SEE BOTH SIDES OF IT.
//
// The first binding built every leaf with a front and a back, at both widths.
// On a spread that is exactly a book: turn the leaf and its back becomes the
// new left-hand page. On a phone it is a trap — the leaf is clipped to a
// single page and swings clear of that box, so turning it reveals the NEXT
// LEAF'S FRONT and its own back is never shown at all. Every even page was
// unreachable on a phone: half the manual, including two of the three charms.
// A reader asked where the second tool was, which is exactly the question a
// manual missing half its pages produces.
//
// So the binding follows the layout. A spread gets leaves of two pages; a
// single column gets a stack of one-page leaves, where turning the top one
// reveals the page beneath. The reading position is kept as a PAGE, not as a
// leaf, so switching between them lands you where you were.
const book = document.getElementById('book') as any;
const whereEl = document.getElementById('where') as any;
const prevEl = document.getElementById('prev') as any;
const nextEl = document.getElementById('next') as any;
const NARROW = matchMedia('(max-width: 860px)');

let leaves: any[] = [];
let single = NARROW.matches;
let page = 0;        // index into PAGES of the page being read (left one, on a spread)
let at = 0;          // leaves currently turned

/** The head of a flowing page: its title, numbered from the second page on, and the lede on the first. */
const flowHead = (el: any, spec: Flow, part: number) => {
  el.appendChild(h('h2', null, spec.title + (part < NUMERAL.length ? NUMERAL[part] : '  ·  ' + (part + 1))));   // NUMERAL[0] is the empty string, and an empty string is falsy: every flow's FIRST page read "· 1" (2026-09-18)
  if (part === 0) {
    if (spec.lede) el.appendChild(h('p', 'lede', spec.lede));
    for (const make of spec.intro || []) el.appendChild(make());   // prose that belongs above the list, on the first page only
  }
};
/** One hidden leaf of the real size, to pour into. Inside the book, so it has the book's own CSS. */
function measuringSheet() {
  const leaf = h('div', 'leaf');
  leaf.style.cssText = 'visibility:hidden;position:absolute;left:-10000px;top:0';
  const face = h('div', 'face front');
  const sheet = h('div', 'sheet');
  face.appendChild(sheet); leaf.appendChild(face); book.appendChild(leaf);
  return { sheet, done: () => leaf.remove() };
}
/** PAGES, with every flowing page poured into as many pages as its list needs at this size. */
function expand(): Array<(el: any) => any> {
  const out: Array<(el: any) => any> = [];
  const m = measuringSheet();
  for (const spec of PAGES) {
    if (typeof spec === 'function') { out.push(spec); continue; }
    const f = spec as Flow;
    /* THE CLOSING NOTE IS POURED WITH THE REST (2026-09-18): appended after the measuring it was not part of, it
     * pushed three pages eleven to thirty-three pixels past the fold. It is the list's last item. */
    const items = f.note ? [...f.items, () => h('p', 'note', f.note)] : f.items;
    const groups: number[][] = [];
    let part = 0, group: number[] = [], into: any = null;
    const start = () => {
      m.sheet.innerHTML = '';
      flowHead(m.sheet, f, part);
      into = m.sheet;
      if (f.wrap) { const [outer, inner] = f.wrap(); m.sheet.appendChild(outer); into = inner; }
    };
    start();
    for (let i = 0; i < items.length; i++) {
      const node = items[i]();
      into.appendChild(node);
      const over = m.sheet.scrollHeight > m.sheet.clientHeight + 1;
      if (over && group.length) {   // one item to a page at the very least, or a long note would loop for ever
        node.remove();
        groups.push(group); group = []; part++;
        start();
        i--;
        continue;
      }
      group.push(i);
    }
    if (group.length) groups.push(group);
    if (!groups.length) groups.push([]);
    groups.forEach((g, i) => out.push((el: any) => {
      flowHead(el, f, i);
      let box: any = el;
      if (f.wrap) { const [outer, inner] = f.wrap(); el.appendChild(outer); box = inner; }
      for (const k of g) box.appendChild(items[k]());
    }));
  }
  m.done();
  return out;
}
let FLAT: Array<(el: any) => any> = [];

function buildFace(side, idx) {
  const face = h('div', 'face ' + side);
  if (idx != null && idx < FLAT.length) {
    const sheet = h('div', 'sheet');
    /* A page renderer either returns a little metadata (`{ plain: true }`
     * suppresses the folio) or nothing at all. Typed here rather than at
     * fourteen definitions. */
    const meta: { plain?: boolean } = (FLAT[idx] as any)(sheet) || {};
    face.appendChild(sheet);
    if (!meta.plain) face.appendChild(h('div', 'folio', 'rako  ·  ' + (idx + 1)));
  }
  return face;
}

function build() {
  book.innerHTML = '';
  leaves = [];
  FLAT = expand();   // measured at this size: a list that has grown takes another page (2026-09-18)
  // The arithmetic is in `binding.js`, as a pure function, because getting it
  // wrong here made half the book unreachable and nothing noticed.
  for (const spec of binding(FLAT.length, single)) {
    const leaf = h('div', 'leaf');
    leaf.appendChild(buildFace('front', spec.front));
    // A one-page leaf still needs a back, or the paper is see-through while it
    // turns. It carries no page — it is the reverse of the sheet.
    leaf.appendChild(buildFace('back', spec.back));
    const fwd = h('div', 'corner fwd'); fwd.title = 'turn the page';
    const back = h('div', 'corner back'); back.title = 'back a page';
    fwd.addEventListener('click', (e) => { e.stopPropagation(); turn(1); });
    back.addEventListener('click', (e) => { e.stopPropagation(); turn(-1); });
    leaf.querySelector('.face.front').appendChild(fwd);
    leaf.querySelector('.face.back').appendChild(back);
    book.appendChild(leaf);
    leaves.push(leaf);
  }
}

/** THE STACKING ORDER HAS TO BE RECOMPUTED ON EVERY TURN. A leaf mid-rotation
 *  sweeps through the space its neighbours occupy; without this the page being
 *  turned passes visibly through the block on the other side. */
function restack() {
  leaves.forEach((leaf, i) => {
    leaf.classList.toggle('turned', i < at);
    leaf.style.zIndex = String(i < at ? i : leaves.length - i);
  });
  page = single ? at : Math.max(0, at * 2 - 1);
  const shown = single ? [page]
    : (at === 0 ? [0] : [at * 2 - 1, at * 2].filter((i) => i < FLAT.length));
  whereEl.textContent = at === 0 ? 'cover'
    : (at >= leaves.length ? 'end'
      : shown.map((i) => i + 1).join('–'));
  prevEl.disabled = at === 0;
  nextEl.disabled = at >= leaves.length;
}

function turn(d) {
  at = Math.max(0, Math.min(leaves.length, at + d));
  restack();
}

/** Rebuild for the other layout, keeping the reader where they were. */
function relayout() {
  const wasSingle = single;
  single = NARROW.matches;
  if (single === wasSingle) return;
  const keep = page;
  build();
  at = single ? Math.min(leaves.length, keep) : Math.min(leaves.length, Math.ceil(keep / 2));
  restack();
}
NARROW.addEventListener('change', relayout);
addEventListener('resize', relayout);

nextEl.addEventListener('click', () => turn(1));
prevEl.addEventListener('click', () => turn(-1));
addEventListener('keydown', (e) => {
  if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); turn(1); }
  else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); turn(-1); }
});
// ...and a swipe, since this is mostly going to be read on a phone
let swipeX = null;
book.addEventListener('pointerdown', (e) => { swipeX = e.clientX; });
book.addEventListener('pointerup', (e) => {
  if (swipeX === null) return;
  const dx = e.clientX - swipeX;
  swipeX = null;
  if (Math.abs(dx) > 40) turn(dx < 0 ? 1 : -1);
});

build();
restack();
